/**
 * StoreFilter Component
 * @module views/components/StoreFilter
 */
import './StoreFilter.scss';

import React, { useCallback } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import * as utils from './StoreFilter';

/**
 * Represents a Filter component
 * @method
 * @param {StoreFilterProps} props - React properties passed from composition
 * @returns StoreFilter
 */
const StoreFilter = ( props ) => {
  const {
    iconImage,
    message = defaultProps.message,
    isSelectedStore = defaultProps.isSelectedStore,
    selectAction,
    isStoreChecked = defaultProps.isStoreChecked,
    onClick = defaultProps.onClick,
    ariaLabel = defaultProps.ariaLabel,
    closeIcon = defaultProps.closeIcon
  } = props;
  const { loading } = useFiltersColumnContext() || {};

  const isStoreFiltered = isSelectedStore && isStoreChecked;

  const handleClick = useCallback(
    utils.composeHandleClick(
      { selectAction }, { onClick }
    ),
    [selectAction, onClick]
  );

  return (
    <Button
      className={ classNames( 'StoreFilter', {
        'StoreFilter--selected': isStoreFiltered
      } ) }
      onClick={ handleClick }
      loading={ loading }
      variant='unstyled'
      ariaPressed={ isStoreFiltered }
      ariaLabel={ isStoreFiltered ? 'remove' : '' }
    >
      <Icon
        description={ iconImage }
        name={ iconImage }
        title={ iconImage }
        ariaHidden={ true }
      />
      <Text
        htmlTag='span'
        textStyle='body-2'
      >
        { message }
      </Text>
      { isStoreFiltered &&
      <Icon
        description={ closeIcon }
        name={ closeIcon }
        title={ closeIcon }
        ariaHidden={ true }
      />
      }
    </Button>
  );
};

/**
 * @method composehandleClick
 * @summary Method to handle button click logic
 * @param {object} data.selectAction - the action to be performed
 * @param {object} methods.onClick - callback function for the click
 */
export const composeHandleClick = ( data, methods ) => () => {
  const { selectAction } = handleEmptyObjects( data );
  const { onClick } = handleEmptyObjects( methods );

  if( !selectAction || !onClick ){
    return;
  }

  onClick( selectAction );
};

/**
 * Default values for passed properties
 * @type {object}
 *@property {string} ariaLabel - Text for the screen readers
 * @property {string} message - Label text for the filter
 * @property {object} isSelectedStore - Indicates if the store is selected
 * @property {boolean} isStoreChecked - Indicates if the store filter is checked
 * @property {function} selectAction - Action triggered on selection
 * @property {function} onClick - Click handler function
 * @property {string} iconImage - Icon image name
 * @property {string} closeIcon - Close icon name
 */
export const defaultProps = {
  ariaLabel: '',
  message: '',
  isSelectedStore: {},
  isStoreChecked: false,
  onClick: null,
  closeIcon: ''
};

/**
 * Property type definitions
 * @typedef StoreFilterProps
 * @type {object}
 * @property {string} ariaLabel - Text for the screen readers
 * @property {string} message - Label text for the filter
 * @property {object} isSelectedStore - Indicates if the store is selected
 * @property {boolean} isStoreChecked - Indicates if the store filter is checked
 * @property {object} selectAction - Action triggered on selection
 * @property {function} onClick - Click handler function
 * @property {string} iconImage - Icon image name
 * @property {string} closeIcon - Close icon name
 */
export const propTypes = {
  /** Text for the screen readers */
  ariaLabel: PropTypes.string,
  /** Label text for the filter */
  message: PropTypes.string,
  /** Indicates if the store is selected */
  isSelectedStore: PropTypes.object,
  /** Indicates if the store filter is checked */
  isStoreChecked: PropTypes.bool,
  /** Action triggered on selection */
  selectAction: PropTypes.object,
  /** Click handler function */
  onClick: PropTypes.func,
  /** Icon image name */
  iconImage: PropTypes.string,
  /** Close icon name */
  closeIcon: PropTypes.string
};

StoreFilter.propTypes = propTypes;
StoreFilter.defaultProps = defaultProps;

export default StoreFilter;
